import {Box, capitalize, Tooltip} from '@mui/material'
import {Checkbox, Column} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useNavigate} from 'react-router-dom'
import {hasCopyCameraConfigPermission, ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import CheckboxTable from 'src/components/CheckboxTable'
import {DataTablePagination} from 'src/components/data-table-pagination'
import LinkWithAction from 'src/components/LinkWithAction'
import RowHover from 'src/components/RowHover/RowHover'
import TableData from 'src/components/table/components/tableData/TableData'
import {TableHeaderColumn, TableHeaderRow} from 'src/components/table/components/tableHeader/TableHeaderBase'
import Table from 'src/components/table/Table'
import {useAuth} from 'src/contexts/AuthContext'
import {useCompany} from 'src/contexts/CompanyContext'
import {Camera} from 'src/services/api/endpoints/CameraEndpoint'
import createStyleSheet from 'src/utilities/createStyleSheet'
import {datesDiffToString} from 'src/utilities/datetimeUtils'
import {useWindowSize} from 'usehooks-ts'
import TriggerAlert from '../../../images/triggerAlert.svg'
import {useCameraContext} from '../context/CamerasContext'
import {CameraStatusConection} from './CameraStatusConection'
import {CameraTableOption} from './CameraTableOption'

type CameraGridTableProps = {
  deleteCamera: () => void
  setCameraOnline: (id: number) => void
  setCameraOffline: () => void
  setOpenCopyConfigModal: (open: Camera) => void
}

type RenderableCameraObject = {
  id: number
  name: string
  client?: string
  origin?: string
  status: string
  online: boolean
  hasActiveTrigger: boolean | undefined
  lastTriggerReceived: string
  self: Camera
}

const columnsForUserBC2: Column[] = [
  {
    id: 'checkbox',
    name: '',
  },
  {
    id: 'connection',
    name: '',
  },
  {
    id: 'name',
    name: 'Nome do Dispositivo',
  },
  {
    id: 'status',
    name: 'Status',
  },
  {
    id: 'contextMenu',
    name: '',
  },
]

const columsForUserB2B: Column[] = [
  {
    id: 'checkbox',
    name: '',
  },
  {
    id: 'connection',
    name: '',
  },
  {
    id: 'name',
    name: 'Nome do Dispositivo',
  },
  {
    id: 'client',
    name: 'Cliente',
  },
  {
    id: 'company',
    name: 'Empresa',
  },
  {
    id: 'origin',
    name: 'Origem',
  },
  {
    id: 'status',
    name: 'Status',
  },
  {
    id: 'contextMenu',
    name: '',
  },
]

function CamerasGridTable(props: CameraGridTableProps) {
  const navigate = useNavigate()
  const hasReadLiveStreamPermission = ValidateUserPermissions('livestream:read')
  const hasReadCameraPermission = ValidateUserPermissions('cameras:read')
  const hasUpdateCameraPermission = ValidateUserPermissions('cameras:update')
  const hasDeleteCameraPermission = ValidateUserPermissions('cameras:delete')
  const hasCopyConfigPermission = hasCopyCameraConfigPermission()
  const {cameras, loadingCameras, camerasTotalCount, camerasPage, setCamerasPage, setCamerasPageSize, selectedCamerasIds, setSelectedCamerasIds} =
    useCameraContext()
  const {deleteCamera, setCameraOnline, setCameraOffline, setOpenCopyConfigModal} = props
  const {getCompanyName} = useCompany()
  const {userIsB2c} = useAuth()
  const {width} = useWindowSize()
  const {enqueueSnackbar} = useSnackbar()

  const tableControl = {
    pageIndex: camerasPage.page,
    pageSize: camerasPage.pageSize,
    total: camerasTotalCount,
    setPageSize: setCamerasPageSize,
    setPageIndex: setCamerasPage,
    nextPage: () => {
      setCamerasPage(camerasPage.page + 1)
    },
    previousPage: () => {
      setCamerasPage(camerasPage.page - 1)
    },
  }

  function getLastTriggerReceived(camera: Camera, days: number, hours: number, minutes: number) {
    if (camera.lastDetection === null) return 'Câmera ainda não enviou nenhum disparo'

    if (days > 1)
      return `Há ${days ? days + ' dias' : ''}${days && hours ? ',' : ''} ${hours ? hours + ' horas' : ''} ${minutes && (days || hours) ? 'e' : ''} ${
        minutes ? minutes + ' minutos' : ''
      } sem detecção de eventos`

    return ''
  }

  const rows = cameras.map((camera) => {
    const lastTriggerDate = camera.lastDetection ? new Date(camera.lastDetection) : new Date()
    const {days, hours, minutes} = datesDiffToString(lastTriggerDate)

    if (userIsB2c) {
      return {
        ...camera,
        id: camera.id,
        name: camera.name,
        status: capitalize(camera.status.description.toLowerCase()),
        online: camera.configuration.isOnline,
        client: camera.client.name,
        self: camera,
        hasActiveTrigger: camera.hasActiveTrigger,
        lastTriggerReceived: getLastTriggerReceived(camera, days, hours, minutes) || '',
      }
    }

    return {
      ...camera,
      id: camera.id,
      name: camera.name,
      client: camera.client.name,
      origin: camera.origin || '-',
      status: capitalize(camera.status.description.toLowerCase()),
      online: camera.configuration.isOnline,
      self: camera,
      hasActiveTrigger: camera.hasActiveTrigger,
      lastTriggerReceived: getLastTriggerReceived(camera, days, hours, minutes) || '',
    }
  })

  const renderConnectionColumn = (row: RenderableCameraObject) => {
    const daysLastTrigger = row.lastTriggerReceived

    const status = row.online ? 'habilitada' : 'desabilitada'
    return (
      <Box sx={{display: 'flex', gap: '10px', padding: '0px 10px', justifyContent: 'start'}}>
        <Tooltip title='Status da conexão com a câmera' placement='top'>
          <Box sx={{cursor: 'pointer'}}>
            <CameraStatusConection camera={row.self} />
          </Box>
        </Tooltip>

        <Tooltip title={`Câmera ${status} no Brain `} placement='top'>
          <Box sx={{cursor: 'pointer'}}> {row.online ? '🟢' : '🔴'}</Box>
        </Tooltip>

        {daysLastTrigger === '' ? (
          <></>
        ) : (
          <>
            <Tooltip title={row.lastTriggerReceived} placement='top'>
              <Box sx={{cursor: 'pointer'}}>
                <img src={TriggerAlert} alt='Alerta' />
              </Box>
            </Tooltip>
          </>
        )}
      </Box>
    )
  }

  const renderContextMenu = (row: RenderableCameraObject) => {
    return (
      <>
        {selectedCamerasIds.length <= 1 && (
          <CameraTableOption
            oneObject={row.id.toString()}
            onClick={(event) => {
              if (event === 'to-events') return navigate(`/events?camera=${row.name}`)
              if (event === 'edit-camera') navigate(row.id.toString())
              if (event === 'copy-config') setOpenCopyConfigModal(row.self)
              if (event === 'delete') {
                setSelectedCamerasIds([+row.id])
                deleteCamera()
              }
              if (event === 'online') {
                setSelectedCamerasIds([+row.id])
                setCameraOnline(+row.id)
              }
              if (event === 'offline') {
                setSelectedCamerasIds([+row.id])
                setCameraOffline()
              }
            }}
            hasDeletePermission={hasDeleteCameraPermission}
            hasReadPermission={hasReadCameraPermission}
            hasUpdatePermission={hasUpdateCameraPermission}
            hasCopyConfigPermission={hasCopyConfigPermission}
            isCameraOnline={row.online}
          />
        )}
      </>
    )
  }

  const onClickOpenInNewTab = (id: number, cameraOrigin: string) => {
    if (!hasReadLiveStreamPermission && (cameraOrigin === 'RTSP' || cameraOrigin === 'RTMP')) {
      enqueueSnackbar('Você não tem permissão para acessar esse recurso!', {variant: 'warning'})
      return
    }

    window.open(`/registers/devices/${id}`, '')
  }

  const onClickCamera = (id: number, cameraOrigin: string) => {
    if (!hasReadLiveStreamPermission && (cameraOrigin === 'RTSP' || cameraOrigin === 'RTMP')) {
      enqueueSnackbar('Você não tem permissão para acessar esse recurso!', {variant: 'warning'})
      return
    }

    navigate(id.toString())
  }

  const onSelectAll = () => {
    if (selectedCamerasIds.length) return setSelectedCamerasIds([])

    const allIds = rows.map((row) => row.id)
    setSelectedCamerasIds(allIds)
  }

  const customHeader = (columns: Column[]) => {
    return (
      <thead>
        <TableHeaderRow>
          {columns.map((col) =>
            col.id === 'checkbox' ? (
              <TableHeaderColumn>
                <div style={{paddingLeft: '10px'}}>
                  <Checkbox id='check' checked={rows.length ? selectedCamerasIds.length === rows.length : false} onChange={onSelectAll} />
                </div>
              </TableHeaderColumn>
            ) : (
              <TableHeaderColumn>{col.name}</TableHeaderColumn>
            ),
          )}
        </TableHeaderRow>
      </thead>
    )
  }

  return (
    <Box sx={styles.gridItem}>
      <Box flex={1} display={'flex'} flexDirection={'row'}>
        <Table
          columns={userIsB2c ? columnsForUserBC2 : columsForUserB2B}
          isLoading={loadingCameras && !rows.length}
          skeletonRowsAmount={10}
          height='100%'
          tableHeaderChildren={customHeader(userIsB2c ? columnsForUserBC2 : columsForUserB2B)}>
          {rows.map((row, index) => (
            <RowHover id={`row-${row.id}`} key={index}>
              <TableData maxWidthResponsive='30px' minWidthResponsive='30px' paddingLeft='10px'>
                <CheckboxTable id={row.id} selectedIds={selectedCamerasIds} setSelectedIds={setSelectedCamerasIds} />
              </TableData>
              <TableData width={width <= 768 ? undefined : '16px'}>{renderConnectionColumn(row)}</TableData>
              <TableData>
                <LinkWithAction onAuxClick={() => onClickOpenInNewTab(row.id, row.origin)} onClick={() => onClickCamera(row.id, row.origin)}>
                  {row.name}
                </LinkWithAction>
              </TableData>
              {!userIsB2c && <TableData>{row.client}</TableData>}
              {!userIsB2c && <TableData>{getCompanyName(row.self.companyId)}</TableData>}
              {!userIsB2c && <TableData>{row.origin}</TableData>}
              <TableData>{row.status}</TableData>
              <TableData>{renderContextMenu(row)}</TableData>
            </RowHover>
          ))}
        </Table>
      </Box>

      {!loadingCameras && rows.length === 0 ? (
        <Box sx={styles.noRegisters}>Nenhum registro encontrado</Box>
      ) : (
        <Box width={'100%'} display={'flex'} justifyContent={'flex-end'} margin={'15px 0px'}>
          <DataTablePagination table={tableControl} />
        </Box>
      )}
    </Box>
  )
}
const styles = createStyleSheet({
  gridItem: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    padding: '0px 20px',
    height: '100%',
    minHeight: '73vh',
  },
  links: {
    fontFamily: "'Inter', 'sans-serif'",
    fontSize: '14px',
    marginRight: 1,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      color: '#00639f',
    },
  },
  noRegisters: {
    fontFamily: "'Inter', 'sans-serif'",
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 450,
    minHeight: '68vh',
    '@media(max-width:1540px)': {
      fontSize: '0.95em',
    },
  },
})

export default CamerasGridTable
